/**
 * Browse-by-distance.
 *
 * The nearby view answers a different question from the main list. The list
 * asks "what is on", ordered by when; this asks "what is near me", and a
 * dancer deciding whether a night is worth the drive wants the close things
 * first — but still needs to know which of them is tonight and which is three
 * weeks out. So results are bucketed by how soon they are and ordered by
 * distance inside each bucket: near-and-soon rises to the top without a dance
 * two miles away in November burying one six miles away tonight.
 */

import { getDistanceMiles, ANY_DISTANCE } from "./distance";

/** The fields this module needs from an event. */
export type LocatableEvent = {
  date: string; // ISO "2026-02-15"
  venueLatitude?: number;
  venueLongitude?: number;
};

export type Origin = { latitude: number; longitude: number };

export type WithDistance<T> = T & { distanceMiles: number };

/**
 * Splits events into those we can place and those we cannot.
 *
 * Events with no coordinates are handed back rather than dropped. A venue
 * whose address never geocoded is a data gap, not an event that is far away,
 * and silently omitting it makes the calendar look emptier than it is — the
 * caller shows a count so the reader knows something is missing.
 */
export function withDistance<T extends LocatableEvent>(
  events: T[],
  origin: Origin
): { located: WithDistance<T>[]; missingCoords: T[] } {
  const located: WithDistance<T>[] = [];
  const missingCoords: T[] = [];

  for (const event of events) {
    // Not `!lat`: 0 is a real coordinate, even if no New England venue sits on
    // the equator or the prime meridian.
    if (event.venueLatitude === undefined || event.venueLongitude === undefined) {
      missingCoords.push(event);
      continue;
    }
    located.push({
      ...event,
      distanceMiles: getDistanceMiles(
        origin.latitude,
        origin.longitude,
        event.venueLatitude,
        event.venueLongitude
      ),
    });
  }

  return { located, missingCoords };
}

/** Events within `radiusMiles` of the origin. `ANY_DISTANCE` keeps them all. */
export function withinRadius<T extends { distanceMiles: number }>(
  events: T[],
  radiusMiles: number
): T[] {
  if (radiusMiles === ANY_DISTANCE) return events;
  return events.filter((e) => e.distanceMiles <= radiusMiles);
}

export type TimeBucketKey = "tonight" | "tomorrow" | "thisWeek" | "thisMonth" | "later";

export type TimeBucket<T> = {
  key: TimeBucketKey;
  label: string;
  events: T[];
};

/**
 * Which bucket a date falls in, as a count of days from today.
 *
 * The boundaries are deliberately coarse. "Tonight" and "tomorrow" are
 * decisions a dancer makes now; past that the question becomes planning, and
 * splitting it any finer than week/month/beyond just makes more headers.
 */
const BUCKETS: { key: TimeBucketKey; label: string; maxDaysOut: number }[] = [
  { key: "tonight", label: "Tonight", maxDaysOut: 0 },
  { key: "tomorrow", label: "Tomorrow", maxDaysOut: 1 },
  { key: "thisWeek", label: "This week", maxDaysOut: 7 },
  { key: "thisMonth", label: "In the next month", maxDaysOut: 30 },
  { key: "later", label: "Further out", maxDaysOut: Infinity },
];

const DAY_MS = 86_400_000;

/** Whole days from `today` to `date`, both ISO civil dates. */
function daysBetween(today: string, date: string): number {
  // UTC on both sides so a local timezone cannot shift either day.
  return Math.round(
    (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS
  );
}

/**
 * Groups events by how soon they are, nearest venue first within each group.
 *
 * Empty buckets are dropped, so a search that only turns up two dances in
 * October does not render three empty headers above them. Anything already in
 * the past sits in the soonest bucket rather than vanishing — the caller has
 * filtered to upcoming events, and a date that slipped past midnight during a
 * session is still the thing the reader was looking at.
 */
export function bucketByTime<T extends { date: string; distanceMiles: number }>(
  events: T[],
  today: string
): TimeBucket<T>[] {
  const byKey = new Map<TimeBucketKey, T[]>();

  for (const event of events) {
    const daysOut = daysBetween(today, event.date);
    const bucket =
      BUCKETS.find((b) => daysOut <= b.maxDaysOut) ?? BUCKETS[BUCKETS.length - 1]!;
    const existing = byKey.get(bucket.key);
    if (existing) existing.push(event);
    else byKey.set(bucket.key, [event]);
  }

  return BUCKETS.flatMap((bucket) => {
    const bucketEvents = byKey.get(bucket.key);
    if (!bucketEvents || bucketEvents.length === 0) return [];
    // Distance decides the order; a tie falls back to the sooner date and then
    // the earlier start, so the order is stable rather than arbitrary.
    bucketEvents.sort(
      (a, b) => a.distanceMiles - b.distanceMiles || a.date.localeCompare(b.date)
    );
    return [{ key: bucket.key, label: bucket.label, events: bucketEvents }];
  });
}
